import React from 'react';
import { X } from 'lucide-react';

interface EmailPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  emailType: 'verification' | 'order_confirmation' | 'password_reset' | 'notification' | 'supplier_verification' | 'document_notification';
  emailData: any;
}

const EmailPreview: React.FC<EmailPreviewProps> = ({ isOpen, onClose, emailType, emailData }) => {
  if (!isOpen) return null;

  // Generate email content based on type
  const getEmailContent = () => {
    switch (emailType) {
      case 'verification':
        return getVerificationEmail(emailData);
      case 'order_confirmation':
        return getOrderConfirmationEmail(emailData);
      case 'password_reset':
        return getPasswordResetEmail(emailData);
      case 'notification':
        return getNotificationEmail(emailData);
      case 'supplier_verification':
        return getSupplierVerificationEmail(emailData);
      case 'document_notification':
        return getDocumentNotificationEmail(emailData);
      default:
        return <div>No preview available</div>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Email Preview</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {getEmailContent()}
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Email template components
const getVerificationEmail = (data: { name: string; verificationLink: string }) => (
  <div className="border border-gray-200 p-4 rounded-lg">
    <div style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)', padding: '20px', textAlign: 'center', color: 'white', borderRadius: '8px 8px 0 0' }}>
      <h1 style={{ margin: 0 }}>Welcome to Made in BD!</h1>
    </div>
    <div style={{ padding: '20px', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
      <p>Hello {data.name},</p>
      <p>Thank you for creating an account with Made in BD. Please verify your email address to complete your registration.</p>
      <div style={{ textAlign: 'center', margin: '30px 0' }}>
        <a href={data.verificationLink} style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)', color: 'white', padding: '12px 24px', textDecoration: 'none', borderRadius: '6px', fontWeight: 'bold' }}>Verify Email Address</a>
      </div>
      <p>If you didn't create this account, you can safely ignore this email.</p>
      <p>Best regards,<br />The Made in BD Team</p>
    </div>
  </div>
);

const getOrderConfirmationEmail = (data: { 
  name: string; 
  orderDetails: {
    orderNumber: string;
    orderDate: string;
    totalAmount: number;
    items: Array<{
      name: string;
      quantity: number;
      price: number;
    }>;
    shippingAddress: string;
    paymentMethod: string;
  }
}) => (
  <div className="border border-gray-200 p-4 rounded-lg">
    <div style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)', padding: '20px', textAlign: 'center', color: 'white', borderRadius: '8px 8px 0 0' }}>
      <h1 style={{ margin: 0 }}>Order Confirmation</h1>
      <p>Order #{data.orderDetails.orderNumber}</p>
    </div>
    <div style={{ padding: '20px', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
      <p>Hello {data.name},</p>
      <p>Thank you for your order! We're pleased to confirm that we've received your order and it's being processed.</p>
      
      <div style={{ backgroundColor: '#f9fafb', padding: '15px', margin: '20px 0', borderRadius: '6px' }}>
        <h2 style={{ marginTop: 0, color: '#1f2937' }}>Order Summary</h2>
        <p><strong>Order Number:</strong> {data.orderDetails.orderNumber}</p>
        <p><strong>Order Date:</strong> {data.orderDetails.orderDate}</p>
        <p><strong>Payment Method:</strong> {data.orderDetails.paymentMethod}</p>
        <p><strong>Shipping Address:</strong> {data.orderDetails.shippingAddress}</p>
      </div>
      
      <h3 style={{ color: '#1f2937' }}>Order Items</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f3f4f6' }}>
            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Item</th>
            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Qty</th>
            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Price</th>
            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {data.orderDetails.items.map((item, index) => (
            <tr key={index}>
              <td style={{ padding: '10px', borderBottom: '1px solid #e5e7eb' }}>{item.name}</td>
              <td style={{ padding: '10px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>{item.quantity}</td>
              <td style={{ padding: '10px', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>${item.price.toFixed(2)}</td>
              <td style={{ padding: '10px', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>${(item.quantity * item.price).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>Total:</td>
            <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>${data.orderDetails.totalAmount.toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
      
      <div style={{ marginTop: '30px' }}>
        <p>If you have any questions about your order, please contact our customer service team at <a href="mailto:support@madeinbd.com" style={{ color: '#f97316' }}>support@madeinbd.com</a>.</p>
        <p>Best regards,<br />The Made in BD Team</p>
      </div>
    </div>
  </div>
);

const getPasswordResetEmail = (data: { name: string; resetLink: string }) => (
  <div className="border border-gray-200 p-4 rounded-lg">
    <div style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)', padding: '20px', textAlign: 'center', color: 'white', borderRadius: '8px 8px 0 0' }}>
      <h1 style={{ margin: 0 }}>Password Reset Request</h1>
    </div>
    <div style={{ padding: '20px', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
      <p>Hello {data.name},</p>
      <p>We received a request to reset your password for your Made in BD account. Click the button below to reset your password:</p>
      <div style={{ textAlign: 'center', margin: '30px 0' }}>
        <a href={data.resetLink} style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)', color: 'white', padding: '12px 24px', textDecoration: 'none', borderRadius: '6px', fontWeight: 'bold' }}>Reset Password</a>
      </div>
      <p>If you didn't request a password reset, you can safely ignore this email. Your password will remain unchanged.</p>
      <p>This link will expire in 1 hour for security reasons.</p>
      <p>Best regards,<br />The Made in BD Team</p>
    </div>
  </div>
);

const getNotificationEmail = (data: { 
  name: string; 
  subject: string; 
  message: string; 
  actionLink?: string; 
  actionText?: string 
}) => (
  <div className="border border-gray-200 p-4 rounded-lg">
    <div style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)', padding: '20px', textAlign: 'center', color: 'white', borderRadius: '8px 8px 0 0' }}>
      <h1 style={{ margin: 0 }}>Made in BD Notification</h1>
    </div>
    <div style={{ padding: '20px', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
      <p>Hello {data.name},</p>
      <p>{data.message}</p>
      {data.actionLink && data.actionText && (
        <div style={{ textAlign: 'center', margin: '30px 0' }}>
          <a href={data.actionLink} style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)', color: 'white', padding: '12px 24px', textDecoration: 'none', borderRadius: '6px', fontWeight: 'bold' }}>{data.actionText}</a>
        </div>
      )}
      <p>Best regards,<br />The Made in BD Team</p>
    </div>
  </div>
);

const getSupplierVerificationEmail = (data: { name: string; verificationLink: string }) => (
  <div className="border border-gray-200 p-4 rounded-lg">
    <div style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)', padding: '20px', textAlign: 'center', color: 'white', borderRadius: '8px 8px 0 0' }}>
      <h1 style={{ margin: 0 }}>Supplier Account Verified!</h1>
    </div>
    <div style={{ padding: '20px', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
      <p>Hello {data.name},</p>
      <p>Congratulations! Your supplier account on Made in BD has been verified. You can now start listing your products and receiving orders from buyers worldwide.</p>
      <div style={{ textAlign: 'center', margin: '30px 0' }}>
        <a href={data.verificationLink} style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)', color: 'white', padding: '12px 24px', textDecoration: 'none', borderRadius: '6px', fontWeight: 'bold' }}>Access Your Seller Dashboard</a>
      </div>
      <p>If you have any questions or need assistance, please contact our supplier support team at <a href="mailto:suppliers@madeinbd.com" style={{ color: '#f97316' }}>suppliers@madeinbd.com</a>.</p>
      <p>Best regards,<br />The Made in BD Team</p>
    </div>
  </div>
);

const getDocumentNotificationEmail = (data: { 
  name: string; 
  documentType: string; 
  orderNumber: string; 
  documentLink: string 
}) => (
  <div className="border border-gray-200 p-4 rounded-lg">
    <div style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)', padding: '20px', textAlign: 'center', color: 'white', borderRadius: '8px 8px 0 0' }}>
      <h1 style={{ margin: 0 }}>New Document Available</h1>
    </div>
    <div style={{ padding: '20px', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
      <p>Hello {data.name},</p>
      <p>A new document is available for your order #{data.orderNumber}.</p>
      <div style={{ backgroundColor: '#f9fafb', padding: '15px', margin: '20px 0', borderRadius: '6px' }}>
        <h2 style={{ marginTop: 0, color: '#1f2937' }}>{data.documentType}</h2>
        <p>This document has been shared with you by the seller and is now available in your account.</p>
      </div>
      <div style={{ textAlign: 'center', margin: '30px 0' }}>
        <a href={data.documentLink} style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)', color: 'white', padding: '12px 24px', textDecoration: 'none', borderRadius: '6px', fontWeight: 'bold' }}>View Document</a>
      </div>
      <p>If you have any questions about this document, please contact the seller directly through your order messages.</p>
      <p>Best regards,<br />The Made in BD Team</p>
    </div>
  </div>
);

export default EmailPreview;